import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { LogLevel } from '../../../constants/LogLevelConstants';
import { PaymentVendorProcessType } from '../../../constants/PaymentVendorProcessTypeConstants';
import * as CommonStore from '../../../store/Common';

class PaymentusForm extends Component {
    constructor(props) {
        super(props);

        this.guestCheckoutPixelRef = React.createRef();
        this.tokenizationPixelRef = React.createRef();

        const initialConfig = this.getInitialConfig(props.content);

        this.state = {
            sdkLoaded: false,
            token: initialConfig.token || '',
            paymentType: initialConfig.paymentType || 'SMARTGRID',
            activeMethod: props.paymentMethod === "CreditCard" ? "card" : "ach",
            tokenizationOptions: {
                hideLastUsedPm: true,
                availablePaymentMethods: [props.paymentMethod === "CreditCard" ? "card" : "ach"],
                paymentMethodOptions: {
                    card: {
                        styles: {},
                        validationRules: {
                            zipcode: {
                                type: "regex",
                                value: "^[0-9]{5}(?:-[0-9]{4})?$",
                                errorMessage: "Please enter a valid US Zipcode",
                                fieldName: "zipcode"
                            },
                            number: {
                                type: "card",
                                cardTypes: [
                                    "visa",
                                    "mastercard",
                                    "american-express",
                                    "discover"],
                                errorMessage: "Please enter a valid card number",
                                fieldName: "number"
                            }
                        }
                    },
                    ach: {
                        styles: {},
                        placeholders: {
                            nickname: "Bank Account Nickname"
                        }
                    }
                }
            },
            customerInfoConfig: {
                fields: [
                    "customer.first-name",
                    "customer.last-name",
                    "customer.email",
                    "customer.day-phone-nr"
                ]
            },
            paymentDetailsConfig: {
                billDetails: {
                    showAccountNumber: true,
                    showAmountDue: true,
                    showBillInfo: true,
                    showPaymentType: true
                },
                amountEditable: true,
                hidePaymentTypeDescription: true
            },
            translations: {
                "en": {
                    "payment": {
                        "card": {
                            "labels": {}
                        },
                        "ach": {
                            "labels": {
                                "accountTypeOptions": {},
                                "nickname": "Bank Account Nickname"
                            }
                        }
                    }
                }
            }
        };

        this.handleLoad = this.handleLoad.bind(this);
    }

    componentDidMount() {
        const {
            onHideRecessiveFields,
            sourceAccount,
            sourceName
        } = this.props;

        // 1. Handle recessive fields immediately
        if (onHideRecessiveFields) {
            onHideRecessiveFields(['cardAccountName', 'bankAccountName', 'bankNameOnAccount']);
        }

        // 2. Load the external SDK
        this.loadPaymentusSDK();

        // 3. Extract person data safely
        const person = sourceAccount.person ||
            (sourceAccount.persons && sourceAccount.persons[0]) ||
            {};

        // 4. Extract phone number if contacts exist
        if (person.personContacts && person.personContacts.length > 0) {
            person.phoneNumber = person.personContacts[0].contactValue;
        }

        // 5. Build the new state for tokenizationOptions and customer defaults
        const isPaymentMethodSource = sourceName === "PaymentMethod";
        const baseFields = {
            card: ['number', 'expiry', 'cvc', 'name', 'zipcode'],
            ach: ['name', 'accountType', 'routingNumber', 'accountNumber']
        };
        
        const explicitCcFields = isPaymentMethodSource 
            ? ['nickname', ...baseFields.card]
            : baseFields.card;
            
        const explicitAchFields = isPaymentMethodSource
            ? ['nickname', ...baseFields.ach]
            : baseFields.ach;

        this.setState(prevState => {
            const { tokenizationOptions, customerInfoConfig } = prevState;

            return {
                tokenizationOptions: {
                    ...tokenizationOptions,
                    paymentMethodOptions: {
                        ...tokenizationOptions.paymentMethodOptions,
                        card: {
                            ...tokenizationOptions.paymentMethodOptions.card,
                            fields: [
                                ...explicitCcFields
                            ]
                        },
                        ach: {
                            ...tokenizationOptions.paymentMethodOptions.ach,
                            fields: [
                                ...explicitAchFields
                            ]
                        }
                    }
                },
                customerInfoConfig: {
                    ...customerInfoConfig,
                    defaultValues: {
                        "first-name": person.firstName,
                        "last-name": person.lastName,
                        "email": person.emailAddress,
                        "day-phone-nr": person.phoneNumber
                    }
                }
            };
        });
    }

    componentDidUpdate(prevProps) {
        const { content, paymentMethod } = this.props;

        // Force a refresh if the payment method or token content changes
        if (paymentMethod !== prevProps.paymentMethod || content !== prevProps.content) {
            const initialConfig = this.getInitialConfig(content) || {};
            const newToken = initialConfig.token;
            const nextActiveMethod = paymentMethod === "CreditCard" ? "card" : "ach";

            this.setState(prevState => ({
                token: newToken || prevState.token,
                paymentType: initialConfig.paymentType || 'SMARTGRID',
                activeMethod: nextActiveMethod,
                tokenizationOptions: {
                    ...prevState.tokenizationOptions,
                    availablePaymentMethods: [nextActiveMethod]
                }
            }));
        }
    }

    componentWillUnmount() {
        const guestCheckoutPixel = this.guestCheckoutPixelRef.current;
        if (guestCheckoutPixel) {
            guestCheckoutPixel.removeEventListener('CHECKOUT_LOADED', this.onCheckoutLoaded);
            guestCheckoutPixel.removeEventListener('CHECKOUT_ERROR', this.onCheckoutError);
            guestCheckoutPixel.removeEventListener('CHECKOUT_SUCCESS', this.onCheckoutSuccess);
            guestCheckoutPixel.removeEventListener("TOKENIZATION_SUCCESS", this.onTokenizationSuccess);
        }

        const tokenizationPixel = this.tokenizationPixelRef.current;
        if (tokenizationPixel) {
            tokenizationPixel.removeEventListener('TOKENIZATION_SUCCESS', this.onTokenizationSuccess);
            tokenizationPixel.removeEventListener('TOKENIZATION_ERROR', this.onTokenizationError);
        }
    }

    loadPaymentusSDK() {
        const BASE_SRC = "https://js.paymentus.com/sdk/1.2.16/paymentus_sdk.min.js";
        const PIXEL_SRC = "https://js.paymentus.com/sdk/1.2.16/guest_checkout.min.js";
        const CSS_SRC = "https://js.paymentus.com/sdk/1.2.16/themes/light.css";

        // If already loaded, just proceed
        if (document.querySelector(`script[src="${BASE_SRC}"]`) &&
            document.querySelector(`script[src="${PIXEL_SRC}"]`)) {
            this.setState({ sdkLoaded: true }, this.setupEventListeners);
            return;
        }

        // Theme CSS
        if (!document.querySelector(`link[href="${CSS_SRC}"]`)) {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = CSS_SRC;
            document.head.appendChild(link);
        }

        // Base SDK
        const baseScript = document.createElement("script");
        baseScript.src = BASE_SRC;
        baseScript.async = true;

        baseScript.onload = () => {
            // Pixel SDK
            const pixelScript = document.createElement("script");
            pixelScript.src = PIXEL_SRC;
            pixelScript.async = true;

            pixelScript.onload = () => {
                this.setState({ sdkLoaded: true }, this.setupEventListeners);
            };

            document.body.appendChild(pixelScript);
        };

        document.body.appendChild(baseScript);
    }

    setupEventListeners = () => {

        this.onTokenizationSuccess = (event) => {
            const detail = event.detail || {};
            const data = detail.data || {};

            let paymentMethodInfo = {};
            let profileResponse = {};

            // tokenization flow
            if (detail.operation === 'TOKENIZE' && detail.data["profile-response"]) {
                profileResponse = data['profile-response'] || {};
                paymentMethodInfo = { ...profileResponse };
            }

            // guest-checkout flow — cache so onCheckoutSuccess can use it
            if (detail.operation === 'TOKENIZE_RAW') {
                this._cachedPaymentMethodInfo = { ...data };
                return;
            }

            const expDate = paymentMethodInfo['credit-card-expiry-date'];
            const { sourceAccount, sourceName, postServerLog, onPaymentMethodSuccess } = this.props;
            const accountID = sourceAccount ? (sourceAccount.accountID || 0) : 0;

            const accountNumber = paymentMethodInfo['account-number'];
            let bankAccountNumber = '';
            let routingNumber = '';
            let bankAccountType = '';

            if (!expDate && accountNumber) {
                const arrAcctValues = accountNumber.split('-');
                routingNumber = arrAcctValues[0];
                bankAccountNumber = arrAcctValues[1];
                bankAccountType = profileResponse.type === 'CHQ' ? '0' : '1';
            }

            const operation = expDate ? 'SaveCCToken' : 'SaveACHToken';

            const tokenizationResponse = {
                operation: operation,
                cardExpDate: expDate ? (paymentMethodInfo['credit-card-expiry-date'].month + paymentMethodInfo['credit-card-expiry-date'].year.slice(-2)) : '',
                cardNumber: expDate ? accountNumber : '',
                accountNumber: bankAccountNumber,
                routingNumber: routingNumber,
                cardType: expDate ? paymentMethodInfo.type : '',
                bankAccountType: bankAccountType,
                token: paymentMethodInfo.token,
                status: paymentMethodInfo.status,
                holderName: paymentMethodInfo['card-holder-name'],
                nickname: paymentMethodInfo.nickname,
                zip: paymentMethodInfo.zip || paymentMethodInfo.zipCode || paymentMethodInfo['zip-code'] || ''
            };

            if (postServerLog) {
                postServerLog(LogLevel.Info, sourceName + " (operation:" + operation + ", AccountID = " + accountID + "): Paymentus Profile token Success.");
            }

            if (detail.operation === 'TOKENIZE' && onPaymentMethodSuccess) {
                onPaymentMethodSuccess(tokenizationResponse);
            }
        };

        this.onTokenizationError = (event) => {
            const detail = event.detail;
            const { sourceName, sourceAccount, postServerLog, setPaymentError } = this.props;
            const accountID = sourceAccount ? (sourceAccount.accountID || 0) : 0;
            const message = (detail && detail.message) ? detail.message : "Tokenization Error";

            if (postServerLog) {
                postServerLog(
                    LogLevel.Error,
                    sourceName + " (AccountID = " + accountID + "): Paymentus tokenization error. " + message,
                    JSON.stringify(detail)
                );
            }

            if (setPaymentError) {
                setPaymentError(message);
            }
            this.handleLoad();
        };
                
        const guestCheckoutPixel = this.guestCheckoutPixelRef.current;

        if (guestCheckoutPixel) {
            this.onCheckoutLoaded = () => {
                this.handleLoad();
            };

            this.onCheckoutError = (event) => {
                const detail = event.detail;
                const redundantErrors = ["xmlPayment.duplicate"];
                const { sourceName, sourceAccount, postServerLog, setPaymentError } = this.props;
                const accountID = sourceAccount ? (sourceAccount.accountID || 0) : 0;

                if (detail && detail.errors && Array.isArray(detail.errors)) {
                    if (detail.errors.some(function (x) { return x && x.key && redundantErrors.indexOf(x.key) !== -1; })) {
                        return;
                    }
                }

                const message = (detail && detail.message) ? detail.message : "Checkout Error";

                if (postServerLog) {
                    postServerLog(
                        LogLevel.Error,
                        sourceName + " (AccountID = " + accountID + "): Paymentus checkout error. " + message,
                        JSON.stringify(detail)
                    );
                }

                if (setPaymentError) {
                    setPaymentError(message);
                }
                this.handleLoad();
            };

            this.onCheckoutSuccess = (event) => {
                const { sourceAccount, sourceName, postServerLog, onPaymentSuccess, paymentMethod } = this.props;
                const accountID = sourceAccount ? (sourceAccount.accountID || 0) : 0;
                const transactionResponse = JSON.stringify(event.detail[0]);

                if (postServerLog) {
                    postServerLog(LogLevel.Info, sourceName + " (AccountID = " + accountID + "): Paymentus Payment Success.", transactionResponse);
                }

                if (onPaymentSuccess) {
                    const paymentMethodInfo = this._cachedPaymentMethodInfo || {};
                    onPaymentSuccess({
                        vendor: "Paymentus",
                        operation: paymentMethod,
                        accountName: sourceAccount.accountNumber,
                        transactionResponse: transactionResponse,
                        accountNumber: paymentMethodInfo['account-number'],
                        cardNumber: paymentMethodInfo['account-number'],
                        cardType: paymentMethodInfo.type,
                        zip: paymentMethodInfo.zip || paymentMethodInfo.zipCode || paymentMethodInfo['zip-code'] || ''
                    });
                }

                // Clear cache once consumed
                this._cachedPaymentMethodInfo = null;
            };

            guestCheckoutPixel.addEventListener('CHECKOUT_LOADED', this.onCheckoutLoaded);
            guestCheckoutPixel.addEventListener('CHECKOUT_ERROR', this.onCheckoutError);
            guestCheckoutPixel.addEventListener('CHECKOUT_SUCCESS', this.onCheckoutSuccess);
            guestCheckoutPixel.addEventListener('TOKENIZATION_SUCCESS', this.onTokenizationSuccess);
        }
                
        const tokenizationPixel = this.tokenizationPixelRef.current;
        if (tokenizationPixel) {
            this.onTokenizationReady = () => {
                this.handleLoad();
            };

            tokenizationPixel.addEventListener('TOKENIZATION_READY', this.onTokenizationReady);
            tokenizationPixel.addEventListener('TOKENIZATION_SUCCESS', this.onTokenizationSuccess);
            tokenizationPixel.addEventListener('TOKENIZATION_ERROR', this.onTokenizationError);
        }
    };

    getInitialConfig(content) {
        try {
            const data = JSON.parse(CommonStore.decompressString(content) || '{}');
            const { token, paymentType } = data;
            return { token: token, paymentType: paymentType };
        } catch (error) {
            const { sourceName, sourceAccount, postServerLog } = this.props;

            if (postServerLog) {
                postServerLog(LogLevel.Error, `${sourceName} (operation: getInitialConfig, AccountID = ${sourceAccount.accountID}): Cannot decompress iframe config content.`);
            }
            return {};
        }
    }

    handleLoad() {
        if (this.props.onLoaded) {
            this.props.onLoaded();
        }
    }

    renderGuestCheckout() {
        const { customerInfoConfig, tokenizationOptions, paymentDetailsConfig, paymentType } = this.state;
        const { sourceAccount, amount, paymentAttemptID } = this.props;

        const paymentAccount = {
            accountNumber: sourceAccount.accountNumber || sourceAccount.accountID,
            paymentType: paymentType,
            paymentAmount: parseFloat(amount) || 0
        };

        const externalDataMap = JSON.stringify({
            entry: [{ key: "PaymentAttemptID", value: String(paymentAttemptID || 0) }]
        });

        return (
            <guest-checkout-pixel
                ref={this.guestCheckoutPixelRef}
                show-customer-info
                customer-info-config={JSON.stringify(customerInfoConfig)}
                payment-accounts={JSON.stringify([paymentAccount])}
                tokenization-options={JSON.stringify(tokenizationOptions)}
                review-config='["accountNumber",{"id":"paymentDate","formatter":"date"},"paymentMethod","amount"]'
                payment-details-config={JSON.stringify(paymentDetailsConfig)}
                external-data-map={externalDataMap}
                payment-flow="one-page"
                disable-amount-field
                disable-receipt
                hide-pay-later
                skip-to-review
                disable-last-used-payment-method
            />
        );
    }

    renderTokenization() {
        const { activeMethod, tokenizationOptions } = this.state;

        return (
            <tokenization-pixel
                ref={this.tokenizationPixelRef}
                availablePaymentMethods={JSON.stringify([activeMethod])}
                paymentMethodOptions={JSON.stringify(tokenizationOptions.paymentMethodOptions)}
                hideTitle
                hideDefaultPm
            />
        );
    }

    render() {
        const { sdkLoaded, token, activeMethod, translations } = this.state;
        const { paymentVendorProcessTypeID } = this.props;

        if (!sdkLoaded) return null;

        return (
            <div className="paymentus-container" key={activeMethod}>
                <paymentus-base authorization={token} translations={JSON.stringify(translations)} hide-recaptcha-badge>
                    {paymentVendorProcessTypeID === PaymentVendorProcessType.RegularPayment
                        ? this.renderGuestCheckout()
                        : this.renderTokenization()
                    }
                </paymentus-base>
            </div>
        );
    }
}

export default connect(
    state => ({ ...state.common }),
    dispatch => bindActionCreators({ ...CommonStore.actionCreators }, dispatch)
)(PaymentusForm);